function getCmsValue(value) {
  if (value == null) {
    return value;
  }

  if (typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
    return value.value;
  }

  return value;
}

function getCmsImageUrl(value) {
  const resolvedValue = getCmsValue(value);

  if (resolvedValue && typeof resolvedValue === 'object' && resolvedValue.url) {
    return resolvedValue.url;
  }

  return resolvedValue || '';
}

function getCmsHtmlValue(value) {
  const resolvedValue = getCmsValue(value);

  if (resolvedValue == null) {
    return '';
  }

  return typeof resolvedValue === 'string' ? resolvedValue : `${resolvedValue}`;
}

export function getCmsBoolValue(value) {
  return !!getCmsValue(value);
}

export function normalizeAddonId(value) {
  const resolved = getCmsValue(value);

  if (resolved == null) {
    return '';
  }

  return String(resolved).trim().toUpperCase();
}

export function isAddonId(addon, addonId, cmsData) {
  const target = normalizeAddonId(addonId);

  if (!target || !addon) {
    return false;
  }

  const candidates = [
    addon.name,
    cmsData?.id,
    addon.cmsData?.id,
    ...(addon.addonsList || []).flatMap((item) => [item?.name, item?.cmsData?.id]),
  ];

  return candidates.some((candidate) => normalizeAddonId(candidate) === target);
}

export function isEntryPopupEnabled(cmsData) {
  if (!cmsData) {
    return false;
  }

  return getCmsBoolValue(cmsData.isShowEntryPopUp);
}

function buildEntryPopupFieldsFromEpiAddon(addon) {
  return {
    isShowEntryPopUp: getCmsBoolValue(addon.isShowEntryPopUp),
    imageEntryPopUp: getCmsImageUrl(addon.imageEntryPopUp),
    titleEntryPopUp: getCmsHtmlValue(addon.titleEntryPopUp),
    textEntryPopUp: getCmsHtmlValue(addon.textEntryPopUp),
  };
}

export function buildAddonEntryPopupConfigFromEpiAddons(epiAddons) {
  if (!epiAddons?.length) {
    return {};
  }

  return epiAddons.reduce((config, addon) => {
    const id = getCmsValue(addon.id);
    const keys = [id, addon.name].filter(Boolean);
    const entryFields = buildEntryPopupFieldsFromEpiAddon(addon);

    keys.forEach((key) => {
      config[key] = {
        id,
        ...entryFields,
      };
    });

    return config;
  }, {});
}

function getEntryPopupConfigForAddon(addon, entryPopupConfig) {
  const keys = [addon.name, addon.cmsData?.id].filter(Boolean);

  for (const key of keys) {
    if (entryPopupConfig?.[key]) {
      return entryPopupConfig[key];
    }
  }

  return null;
}

export function mergeAddonEntryPopupCms(addon, entryPopupConfig = {}) {
  const popupConfig = getEntryPopupConfigForAddon(addon, entryPopupConfig);

  if (!popupConfig) {
    return addon;
  }

  return {
    ...addon,
    cmsData: {
      ...(addon.cmsData || {}),
      isShowEntryPopUp: popupConfig.isShowEntryPopUp,
      imageEntryPopUp: popupConfig.imageEntryPopUp || addon.cmsData?.imageEntryPopUp,
      titleEntryPopUp: popupConfig.titleEntryPopUp || addon.cmsData?.titleEntryPopUp,
      textEntryPopUp: popupConfig.textEntryPopUp || addon.cmsData?.textEntryPopUp,
    },
  };
}

function mapCmsForEntryPopupDisplay(cmsData = {}) {
  return {
    ...cmsData,
    titleAddonSingle: cmsData.titleEntryPopUp || cmsData.titleAddonSingle,
    descriptionProduct: cmsData.textEntryPopUp || cmsData.descriptionProduct,
    imageAddon: cmsData.imageEntryPopUp || cmsData.imageAddon,
  };
}

function mergeEntryPopupCmsData(itemCmsData = {}, groupCmsData = {}) {
  return mapCmsForEntryPopupDisplay({
    ...itemCmsData,
    isShowEntryPopUp: groupCmsData.isShowEntryPopUp ?? itemCmsData.isShowEntryPopUp,
    imageEntryPopUp: groupCmsData.imageEntryPopUp || itemCmsData.imageEntryPopUp,
    titleEntryPopUp: groupCmsData.titleEntryPopUp || itemCmsData.titleEntryPopUp,
    textEntryPopUp: groupCmsData.textEntryPopUp || itemCmsData.textEntryPopUp,
  });
}

export function mapAddonForEntryPopupDisplay(addon) {
  if (!addon) {
    return addon;
  }

  const groupCmsData = addon.cmsData || {};
  const mappedGroupCms = mapCmsForEntryPopupDisplay(groupCmsData);

  return {
    ...addon,
    cmsData: mappedGroupCms,
    addonsList: addon.addonsList?.map((item) => ({
      ...item,
      cmsData: mergeEntryPopupCmsData(item.cmsData, groupCmsData),
    })),
  };
}

export function getEntryPopupAddons(addons, entryPopupConfig = {}) {
  if (!addons?.length) {
    return [];
  }

  return addons
    .map((addon) => mergeAddonEntryPopupCms(addon, entryPopupConfig))
    .filter((addon) => isEntryPopupEnabled(addon.cmsData));
}

export function getAddonsCount(addons) {
  if (!Array.isArray(addons)) {
    addons = [addons];
  }

  return addons.filter((addon) => addon.action === 'Add').length;
}

export function isDisplayAdd(addonsList) {
  const addons = addonsList?.filter((addon) => addon.action === 'Add');
  const upperLimit = addonsList?.[0]?.upperLimit;

  return addons.length < upperLimit;
}

export function isDisplayRemove(addonsList) {
  const addons = addonsList?.filter((addon) => addon.action === 'Add');
  const lowerLimit = addonsList?.[0]?.lowerLimit;

  return addons.length > lowerLimit;
}
